import { getPruefziffern } from "./checkDigit";
import {
  InvalidBezirksnummerError,
  InvalidPruefzifferError,
  NwInternalConsistencyError,
  UnknownFinanzamtError,
} from "./errors";
import { finanzamtNummern } from "./finanzamtNummern";
import { parseSteuernummer } from "./parseSteuernummer";
import { bayerischerProgrammierverbundStates } from "./stateMapping";
import type { Options } from "./types";

const forbiddenBezirksnummern = new Set([
  "000",
  "998",
  "999",
  "0000",
  "0998",
  "0999",
]);

/** Validates a German Steuernummer. Returns `undefined` if valid, or an error message string if invalid. */
export function validateSteuernummer(
  value: string,
  options?: Options
): string | undefined {
  try {
    const {
      bundesfinanzamtnummer,
      bezirksnummer,
      states,
      unterscheidungsnummer,
      pruefziffer,
      normalizedSteuernummer,
    } = parseSteuernummer(value, options ?? {});

    if (!finanzamtNummern.has(bundesfinanzamtnummer)) {
      throw new UnknownFinanzamtError(bundesfinanzamtnummer);
    }

    if (forbiddenBezirksnummern.has(bezirksnummer)) {
      throw new InvalidBezirksnummerError(bezirksnummer);
    }

    const statesToCheck = Array.isArray(states) ? states : [states];

    const isBayerischerProgrammierverbund = statesToCheck.some((s) =>
      bayerischerProgrammierverbundStates.has(s)
    );
    if (isBayerischerProgrammierverbund && Number(bezirksnummer) < 100) {
      throw new InvalidBezirksnummerError(bezirksnummer);
    }

    if (
      statesToCheck.includes("DE-NW") &&
      Number(unterscheidungsnummer + pruefziffer) < 9
    ) {
      throw new NwInternalConsistencyError();
    }

    const pruefziffern = getPruefziffern(normalizedSteuernummer, states);
    if (
      pruefziffern.length > 0 &&
      !pruefziffern.includes(Number(pruefziffer))
    ) {
      throw new InvalidPruefzifferError(pruefziffern, pruefziffer);
    }

    return undefined;
  } catch (error) {
    return (error as unknown as Error).message;
  }
}
